// Imports
import React, { useRef, useEffect } from "react";
import PropTypes from "prop-types";
import QuizQuestionHeader from "src/modules/QuizQuestionHeader/jsx/index.jsx";
import QuizQuestionOptions from "src/modules/QuizQuestionOptions/jsx/index.jsx";
import QuizQuestionRange from "src/modules/QuizQuestionRange/jsx/index.jsx";
import QuizQuestionBoolean from "src/modules/QuizQuestionBoolean/jsx/index.jsx";
import QuizQuestionSlider from "src/modules/QuizQuestionSlider/jsx/index.jsx";

import { useQuiz } from "src/contexts/QuizContext.js";
import { quizInteractionTracking } from "src/modules/QuizHelpers/index.js";

QuizQuestionBody.propTypes = {
    handleBackButton: PropTypes.func,
};

function QuizQuestionBody({ handleBackButton }) {
    const { dispatch, questions, questionNo, questionSection, quizStatus } =
        useQuiz();

    // create some config for this component
    // Verify the question exists - if not, use questionNo - 1 (we might be on previous question)
    let actualQuestionNo = questionNo;
    let qKey = `question${questionNo}`;
    if (!questions[qKey] && questionNo > 1) {
        actualQuestionNo = questionNo - 1;
        qKey = `question${actualQuestionNo}`;
    }
    const sKey = `section${questionSection}`;
    const subquestionsLength = Object.keys(
        questions[qKey]?.subquestions,
    ).length;

    const config = {
        qKey: qKey,
        sKey: `section${questionSection}`,
        question: questions[qKey],
        subquestion: questions[qKey]?.subquestions[sKey],
        subquestionsLength: subquestionsLength,
    };

    function handleSavedAnswer(dispatchType) {
        dispatch({
            type: dispatchType,
            payload: {
                questionKey: qKey,
                sectionKey: sKey,
                value: config.question.subquestions[sKey].currentAnswer,
            },
        });
    }

    let props = {
        questionNo,
        questionKey: qKey,
        sectionKey: sKey,
        questions: questions,
        question: config.question,
        subquestion: config.subquestion,
        subquestionsLength: config.subquestionsLength,
        optionsLength: config.optionsLength,
        handleBackButton,
        dispatch,
        handleSavedAnswer,
    };

    const questionComponents = {
        QuizQuestionOptions,
        QuizQuestionRange,
        QuizQuestionBoolean,
        QuizQuestionSlider,
    };

    const QuestionOptions = () => {
        props = { ...props, ...(config.subquestion?.props ?? {}) };
        if (
            config.subquestion?.component &&
            questionComponents[config.subquestion?.component]
        ) {
            const Component = questionComponents[config.subquestion?.component];
            return <Component {...props} />;
        } else {
            console.log(`${qKey} component property not found.`);
            return <></>;
        }
    };

    const QuizNextButton = () => {
        let label = "Confirm";
        let dispatchType = "saveAnswer";
        let showButton;
        let navigationAction = "confirm";

        // confirm button for range questions
        if (
            config.question.subquestions[sKey].component ==
                "QuizQuestionRange" ||
            config.question.subquestions[sKey].component ==
                "QuizQuestionOptions" ||
            config.question.subquestions[sKey].component == "QuizQuestionSlider"
        ) {
            if (config.question.subquestions[sKey].currentAnswer) {
                showButton = true;
            }
        }

        // single questions can be confirmed or finished status
        if (
            config.question.status == "confirm" ||
            config.question.status == "finished"
        ) {
            showButton = true;
            label = "Next question";
            dispatchType = "nextQuestion";
            navigationAction = "next";
        }

        // if we editing a question, override the label
        if (quizStatus == "edit") {
            label = "Save Answer";
            navigationAction = "change";
        }

        // Last question and not editing: show Submit instead of Next question
        if (
            label === "Next question" &&
            config.question?.isLast &&
            quizStatus !== "edit"
        ) {
            label = "Submit";
            navigationAction = "submit";
        }

        const getCurrentAnswerIndex = () => {
            const subquestion = config.question.subquestions[sKey];
            if (!subquestion) return null;

            const currentAnswer =
                subquestion.currentAnswer || subquestion.savedAnswer;
            if (!currentAnswer) return null;

            const options = subquestion.options || [];
            const index = options.findIndex((opt) => opt === currentAnswer);
            return index >= 0 ? index + 1 : null; // 1-based index
        };

        // Get question number from questionKey to ensure accuracy
        // (e.g., "question3" -> "Q3")
        const getQuestionNumber = () => {
            return qKey.replace("question", "Q");
        };

        const isSubQuestion = config.subquestionsLength > 1;

        return showButton ? (
            <div className="quiz-question__next-button">
                <button
                    onClick={() => {
                        // Reset focus immediately when next button is clicked
                        resetAllFocus();

                        // Track the interaction
                        if (navigationAction === "next") {
                            // Click on 'Next Question' after answering a question
                            quizInteractionTracking({
                                interaction_type: "click",
                                question_number: getQuestionNumber(),
                                sub_question_index: null,
                                selected_answer_index: null,
                                answer_changed: false,
                                navigation_action: "next",
                            });
                        } else if (navigationAction === "submit") {
                            // Click on 'Submit' to complete the quiz (last question)
                            quizInteractionTracking({
                                interaction_type: "click",
                                question_number: getQuestionNumber(),
                                sub_question_index: null,
                                selected_answer_index: null,
                                answer_changed: false,
                                navigation_action: "submit",
                            });
                        } else if (navigationAction === "confirm") {
                            // Confirm sub-question answer
                            // Check if answer changed by comparing currentAnswer with savedAnswer
                            const subquestion =
                                config.question.subquestions[sKey];
                            const currentAnswer = subquestion?.currentAnswer;
                            const savedAnswer = subquestion?.savedAnswer;
                            const answerChanged = savedAnswer
                                ? currentAnswer !== savedAnswer
                                : false;

                            quizInteractionTracking({
                                interaction_type: "select",
                                question_number: getQuestionNumber(),
                                sub_question_index: isSubQuestion
                                    ? questionSection
                                    : null,
                                selected_answer_index: getCurrentAnswerIndex(),
                                answer_changed: answerChanged,
                                navigation_action: "confirm",
                            });
                        } else if (navigationAction === "change") {
                            // Change an already-answered sub-question
                            // Check if answer changed by comparing currentAnswer with savedAnswer
                            const subquestion =
                                config.question.subquestions[sKey];
                            const currentAnswer = subquestion?.currentAnswer;
                            const savedAnswer = subquestion?.savedAnswer;
                            const answerChanged = savedAnswer
                                ? currentAnswer !== savedAnswer
                                : false;

                            quizInteractionTracking({
                                interaction_type: "select",
                                question_number: getQuestionNumber(),
                                sub_question_index: isSubQuestion
                                    ? questionSection
                                    : null,
                                selected_answer_index: getCurrentAnswerIndex(),
                                answer_changed: answerChanged,
                                navigation_action: "save_answer",
                            });
                        }

                        handleSavedAnswer(dispatchType);
                    }}
                    tabIndex={99}
                >
                    {label}
                </button>
            </div>
        ) : (
            <></>
        );
    };

    // set the qContainer in state
    const qContainerref = useRef();
    useEffect(() => {
        dispatch({
            type: "qContainer",
            payload: qContainerref,
        });
    }, []);

    // Comprehensive focus reset function for mobile devices
    const resetAllFocus = () => {
        // Clear focus from any focused element
        if (document.activeElement && document.activeElement.blur) {
            document.activeElement.blur();
        }

        // Also try to remove focus from any element that might be focused
        if (
            document.activeElement &&
            document.activeElement !== document.body
        ) {
            document.activeElement.blur();
        }

        // Force focus to body to ensure no element is focused
        document.body.focus();
        document.body.blur();

        // Additional mobile-specific focus clearing
        if (window.getSelection) {
            window.getSelection().removeAllRanges();
        }
    };

    // Reset focus when question changes (for mobile devices)
    useEffect(() => {
        resetAllFocus();
    }, [questionNo, questionSection]);

    return (
        <section className="quiz-question">
            {/* QUESTION HEADER CONTAINER */}
            <div className="quiz-question__header-container">
                {questionNo !== 1 && (
                    <p className="quiz-question__expanded-complete">Complete</p>
                )}
                <QuizQuestionHeader {...props} />
            </div>

            {/* QUESTION OPTIONS CONTAINER */}
            <div className="quiz-question__q-container" ref={qContainerref}>
                <div className="quiz-inverse-q-corner">
                    <div className="inverse-corner bottom right turn-270 top-offset q-container--corner"></div>
                </div>

                {/* Hard coding 1st Question options */}
                <QuestionOptions />
                <QuizNextButton />
            </div>
        </section>
    );
}

export default QuizQuestionBody;
